// web-staking-estimator-frontend/src/hooks/useEstimateComputation.js
import { useEffect } from "react";
import { apiPost } from "../lib/api";
import { stripMapImage } from "../utils/geometry";

/**
 * Hook to handle automatic estimate computation when inputs change.
 * Fetches from backend and updates estimate state.
 */
export function useEstimateComputation({
  serviceType,
  selectedPoint,
  requestInfo,
  setEstimate,
  setRequestInfo,
}) {
  useEffect(() => {
    const havePoint =
      Number.isFinite(selectedPoint?.x) &&
      Number.isFinite(selectedPoint?.y) &&
      Number.isFinite(selectedPoint?.wkid);

    // For irrigation, require HP instead of amps
  /*
   * Irrigation uses HP (horsepower) instead of amps for pump sizing
   * Residential/commercial use amps (electrical service capacity)
   * Check which fields are required based on service type
   */
  const isIrrigation = serviceType?.toLowerCase().includes('irrig');
  const haveInputs =
    !!serviceType &&
    Number.isFinite(requestInfo?.svc_phase) &&
    !!requestInfo?.svc_volts &&
    (isIrrigation 
      ? Number.isFinite(requestInfo?.svc_hp) && requestInfo.svc_hp > 0  // Need HP for irrigation
      : Number.isFinite(requestInfo?.svc_amps)) &&                      // Need amps otherwise
    !!requestInfo?.svc_ohug;

    if (!havePoint || !haveInputs) return;

    const payload = {
      serviceType,
      svc_phase: requestInfo.svc_phase,
      svc_volts: requestInfo.svc_volts,
      svc_amps: requestInfo.svc_amps,
      svc_hp: requestInfo.svc_hp,
      svc_ohug: requestInfo.svc_ohug,
      point: {
        x: selectedPoint.x,
        y: selectedPoint.y,
        wkid: selectedPoint.wkid,
      },
    };

    let cancelled = false;

    (async () => {
      try {
        const res = await apiPost("/api/compute", payload);
        if (cancelled) return;  // Don't update state if newer request already started

        if (res.blocked) {
          setEstimate((prev) => ({
            ...prev,
            distanceFeet: res.distanceFeet ?? prev.distanceFeet,
            total: null,
            range: { low: null, high: null },
            lvSubtotal: null,
            mvSubtotal: null,
          }));
          if (requestInfo.matched_service_id) {
            setRequestInfo((prev) => ({
              ...prev,
              matched_service_id: null,
            }));
          }
          return;
        }

        setEstimate((prev) => ({
          ...prev,
          ...stripMapImage(res),
          distanceFeet: Number.isFinite(res.distanceFeet)
            ? res.distanceFeet
            : prev.distanceFeet,
          total: Number.isFinite(res.total) ? res.total : prev.total ?? null,
          range: {
            low: Number.isFinite(res?.range?.low) ? res.range.low : null,
            high: Number.isFinite(res?.range?.high) ? res.range.high : null,
          },
          lvSubtotal: Number.isFinite(res.lvSubtotal) ? res.lvSubtotal : null,
          mvSubtotal: Number.isFinite(res.mvSubtotal) ? res.mvSubtotal : null,
        }));

        if (
          res.matchedServiceId &&
          requestInfo.matched_service_id !== res.matchedServiceId
        ) {
          setRequestInfo((prev) => ({
            ...prev,
            matched_service_id: res.matchedServiceId,
          }));
        }
      } catch (err) {
        console.warn("compute failed:", err);
        setEstimate((prev) => ({
          ...prev,
          total: null,
          range: { low: null, high: null },
          lvSubtotal: null,
          mvSubtotal: null,
        }));
        if (requestInfo.matched_service_id) {
          setRequestInfo((prev) => ({
            ...prev,
            matched_service_id: null,
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serviceType,
    selectedPoint?.x,
    selectedPoint?.y,
    selectedPoint?.wkid,
    requestInfo.svc_phase,
    requestInfo.svc_volts,
    requestInfo.svc_amps,
    requestInfo.svc_hp,
    requestInfo.svc_ohug,
  ]);
}
